import { firstName, money } from "../format";
import {
  type ChartPoint,
  type CoveragePlan,
  type Figure,
  type HeadlineFigure,
  maxLeads,
  type Network,
  type NetworkFigures,
  type Tier,
} from "../read/readCoverage";

/** What's left before a stage ends; a range when the family rule matters. */
export type Span = { low: number; high: number };

type Pair = NetworkFigures["deductible"];

export type Standing = {
  kind: "deductible" | "outOfPocket";
  switchable: boolean;
  initial: Tier;
  /** The figure that decides the lead stage; null when it's a range. */
  deciding: Tier | null;
  deductible: Span | null;
  outOfPocket: Span | null;
};

export const left = (f: Figure | null): number | null =>
  f === null || (f.remaining === null && f.limit === null)
    ? null
    : Math.max(0, f.remaining ?? (f.limit ?? 0) - f.met);

export const metCallout = (
  kind: Standing["kind"],
  tier: Tier,
  figure: Figure | null,
): boolean =>
  // A $0 figure reads "No deductible", never met.
  figure?.limit !== 0 &&
  left(figure) === 0 &&
  (tier === "family" || kind === "outOfPocket");

const kindName = { deductible: "deductible", outOfPocket: "out-of-pocket max" };

const only = (tier: Tier, n: number | null) => ({
  tier,
  span: n === null ? null : { low: n, high: n },
});

/** The deciding figure on one pair. Embedded: each person's counts inside the family's. */
const decide = (
  { individual, family }: Pair,
  embedded: boolean,
): { span: Span | null; tier: Tier | null } => {
  const you = left(individual);
  const them = left(family);
  if (family === null) {
    return only("individual", you);
  }
  if (you === null) {
    return only("family", them);
  }
  if (them === null) {
    return only("individual", you);
  }
  if (embedded || them <= you) {
    return you <= them ? only("individual", you) : only("family", them);
  }
  return { tier: null, span: { low: you, high: them } };
};

/**
 * The out-of-pocket max counts as embedded: the ACA caps each person's. The
 * payer doesn't say how the deductible counts.
 */
export const standingOf = (
  plan: CoveragePlan,
  figures: NetworkFigures = plan.inNetwork,
): Standing => {
  const deductible = decide(figures.deductible, false);
  const outOfPocket = decide(figures.outOfPocket, true);
  const { individual, family } = figures.outOfPocket;
  // The root's headline rule, for this network's figures. The plan's own
  // headline is in network's, so another network falls back to its deductible.
  const fallback =
    figures === plan.inNetwork
      ? plan.headline?.kind
      : (figures.deductible.individual ?? figures.deductible.family) === null
        ? "outOfPocket"
        : "deductible";
  const kind =
    maxLeads(figures) && (individual ?? family) !== null
      ? "outOfPocket"
      : (fallback ?? "deductible");
  const lead = kind === "deductible" ? deductible : outOfPocket;
  const pair = figures[kind];
  const switchable =
    !plan.figuresFromSubscriber &&
    pair.individual !== null &&
    pair.family !== null;
  return {
    kind,
    switchable,
    initial: switchable
      ? (lead.tier ?? "family")
      : pair.individual !== null
        ? "individual"
        : "family",
    deciding: lead.tier,
    deductible: deductible.span,
    outOfPocket: outOfPocket.span,
  };
};

/** What the (i) beside the left column explains: how the member's figure ties to the family's. */
export const noteOf = (
  plan: CoveragePlan,
  { kind, switchable, deciding }: Standing,
  tier: Tier,
  figures: NetworkFigures = plan.inNetwork,
): string | null => {
  const member = firstName(plan);
  if (plan.figuresFromSubscriber) {
    const whose = plan.subscriberName
      ? `${plan.subscriberName}’s`
      : "the subscriber’s";
    return `${member}’s figures come from ${whose} plan, which only reports the family’s.`;
  }
  const { individual, family } = figures[kind];
  const you = left(individual);
  const them = left(family);
  if (!switchable || you === null || them === null) {
    return null;
  }
  const name = kindName[kind];
  if (them === 0) {
    return `The family’s ${name} is met, so ${member}’s is too.`;
  }
  if (kind === "deductible") {
    const asked = `${plan.payerName} hasn’t said whether ${member}’s own ${money(individual?.limit ?? 0)} counts on its own.`;
    return deciding === "family"
      ? `${asked} The family is closer, so it’s ${money(them)} either way.`
      : you === 0
        ? `${asked} If it does, ${member}’s is met.`
        : `${asked} If it does, ${member} is ${money(you)} away.`;
  }
  if (you === 0) {
    return `${member}’s is met. The family still has ${money(them)} to go for everyone else.`;
  }
  const other =
    tier === "individual"
      ? `Family: ${money(them)}`
      : `${member}: ${money(you)}`;
  return `Whichever ${name} is met first, ${member}’s or the family’s, counts for ${member}. ${other} to go.`;
};

/** In network, the plan's headline stands in for a missing figure. */
export const tierFigure = (
  plan: CoveragePlan,
  network: Network,
  kind: Standing["kind"],
  tier: Tier,
): HeadlineFigure | null => {
  const figure = plan[network]?.[kind][tier];
  return figure
    ? { ...figure, kind, tier }
    : network === "inNetwork"
      ? plan.headline
      : null;
};

export const shownTier = (
  { switchable, initial }: Standing,
  picked: Tier | undefined,
): Tier => (switchable && picked ? picked : initial);

export const pickedHeadline = (
  plan: CoveragePlan,
  picked: Tier | undefined,
): HeadlineFigure | null => {
  const standing = standingOf(plan);
  return standing.switchable
    ? tierFigure(plan, "inNetwork", standing.kind, shownTier(standing, picked))
    : plan.headline;
};

/** A met deductible's level, for an out-of-pocket chart to mark; null when unmet or $0. */
export const metDeductibleLevel = (deductible: Figure | null): number | null =>
  deductible?.limit && left(deductible) === 0 ? deductible.limit : null;

/**
 * The first reading at the deductible; null if none reached it, there's none
 * to reach, or the first reading was already at or past it, so the crossing went unseen.
 */
export const crossedAt = (
  history: ChartPoint[],
  deductible: Figure | null,
): Date | null => {
  const limit = deductible?.limit ?? 0;
  const index = history.findIndex(p => p.met >= limit);
  return limit > 0 && index > 0 ? (history[index]?.at ?? null) : null;
};
